// Shared map setup for the guide map and the event page map.
//
// MapLibre GL JS (open source, BSD-3) renders vector tiles from OpenFreeMap
// (open source, free, no API key or usage limits). If OpenFreeMap can't be
// reached we fall back to the standard OpenStreetMap raster tiles.
//
// MapLibre is loaded from the CDN on demand so pages without a map don't pay for it.

const MAPLIBRE_VERSION = "5.24.0"
const MAPLIBRE_JS = {
  url: `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`,
  integrity: "sha384-5+cfbwT0iiub6VsQAdn6yz16nr6sDiQoHx6tm4O8OVYXHYOxcffFmCJBL0dgdvGp"
}
const MAPLIBRE_CSS = {
  url: `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`,
  integrity: "sha384-uTttxo/aOKbdE5RlD/SPzSDoDmNvGlUYPjONi2MN/b7c9HPSvW07OIuyP7uL6jxK"
}

const STYLE_URL = "https://tiles.openfreemap.org/styles/positron"
const STYLE_TIMEOUT_MS = 6000

const FALLBACK_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      maxzoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
    }
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }]
}

let scriptPromise = null
let stylePromise = null

export function loadMapLibre() {
  ensureStylesheet()

  if (window.maplibregl) return Promise.resolve(window.maplibregl)

  scriptPromise ||= new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = MAPLIBRE_JS.url
    script.integrity = MAPLIBRE_JS.integrity
    script.crossOrigin = "anonymous"
    script.onload = () => resolve(window.maplibregl)
    script.onerror = () => {
      script.remove()
      scriptPromise = null
      reject(new Error("Failed to load MapLibre"))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

// Turbo can drop stylesheets we injected into <head>, so check on every map load
function ensureStylesheet() {
  if (document.querySelector(`link[href="${MAPLIBRE_CSS.url}"]`)) return

  const link = document.createElement("link")
  link.rel = "stylesheet"
  link.href = MAPLIBRE_CSS.url
  link.integrity = MAPLIBRE_CSS.integrity
  link.crossOrigin = "anonymous"
  document.head.appendChild(link)
}

export function loadBaseStyle() {
  stylePromise ||= fetchStyle().catch((error) => {
    console.warn("OpenFreeMap unavailable, falling back to OpenStreetMap tiles:", error)
    stylePromise = null
    return FALLBACK_STYLE
  })

  return stylePromise
}

async function fetchStyle() {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), STYLE_TIMEOUT_MS)

  try {
    const response = await fetch(STYLE_URL, { signal: controller.signal })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return await response.json()
  } finally {
    clearTimeout(timeout)
  }
}

export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  })[char])
}

// Teardrop pin in the event type colour. Several events at one venue share a
// single dark pin showing the count, so none of them hide underneath another.
// `icon` is trusted SVG path markup from Event::EVENT_TYPE_ICONS.
// The halo pulses behind the pin while it has the is-live class.
export function createPin({ color, icon, count = 1, label, interactive = true }) {
  const pin = document.createElement(interactive ? "button" : "div")
  pin.className = "map-pin"
  pin.setAttribute("aria-label", label)
  if (interactive) {
    pin.type = "button"
  } else {
    pin.setAttribute("role", "img")
  }
  pin.style.setProperty("--pin-color", count > 1 ? "#1c1917" : (color || "#1c1917"))

  const content = count > 1
    ? `<span class="map-pin-count">${count}</span>`
    : `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="map-pin-icon" aria-hidden="true">${icon || ""}</svg>`

  pin.innerHTML = `<span class="map-pin-halo"></span><span class="map-pin-head"><span class="map-pin-content">${content}</span></span>`
  return pin
}

export function directionsUrl(lat, lng) {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}
